import { Link } from 'react-router';
import { formatXaf, titleCase } from '../lib/format.js';
import { useAuth } from '../state/AuthContext.jsx';
import { useMeta } from '../state/MetaContext.jsx';
import { StatusBadge } from './ui.jsx';
import { formatPlot, isForSale, isMultiUnit, lettingOf, statusBadgeFor, unitsSummary } from '../lib/letting.js';

const HeartIcon = ({ filled }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      d="M12 20.7l-1.5-1.35C5.9 15.36 3 12.75 3 9.5 3 6.96 5 5 7.5 5c1.4 0 2.76.66 3.7 1.74L12 7.7l.8-.96C13.74 5.66 15.1 5 16.5 5 19 5 21 6.96 21 9.5c0 3.25-2.9 5.86-7.5 9.86L12 20.7z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const CameraIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="currentColor">
    <path d="M9 3l-1.2 2H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V7a2 2 0 00-2-2h-3.8L15 3H9zm3 5a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z" />
  </svg>
);

/**
 * The listing tile used on the home page, in search results and in dashboards.
 * `onToggleFavorite` is optional: dashboards pass nothing and the heart hides.
 */
const PropertyCard = ({ property, onToggleFavorite, footer, compact }) => {
  const { user } = useAuth();
  const { propertyTypeLabel, landTitles = [] } = useMeta();

  const place = [property.neighbourhood, property.city].filter(Boolean).join(', ');
  const badge = statusBadgeFor(property);
  const sale = isForSale(property);
  const multi = !sale && isMultiUnit(property);
  const summary = unitsSummary(property);
  const canSave = Boolean(onToggleFavorite) && (!user || user.role === 'tenant');

  return (
    <article className={`property-card ${compact ? 'is-compact' : ''}`}>
      <Link to={`/properties/${property.id}`} className="property-card-media">
        {property.coverImage ? (
          <img src={property.coverImage} alt={property.title} loading="lazy" />
        ) : (
          <span className="property-card-noimage">No photo yet</span>
        )}

        {property.mediaCount > 0 ? (
          <span className="media-count">
            <CameraIcon /> {property.mediaCount}
          </span>
        ) : null}

        {/* Every card says where it stands - available, pending or rented, or
            for sale, under offer or sold - so nobody has to open a listing to
            find out it has gone. */}
        <span className="card-status">
          <StatusBadge status={badge.status} label={badge.label} />
        </span>
      </Link>

      {canSave ? (
        <button
          type="button"
          className={`save-btn ${property.favorited ? 'is-saved' : ''}`}
          onClick={() => onToggleFavorite(property)}
          aria-label={property.favorited ? 'Remove from saved' : 'Save this listing'}
          title={property.favorited ? 'Remove from saved' : 'Save this listing'}
        >
          <HeartIcon filled={property.favorited} />
        </button>
      ) : null}

      <div className="property-card-body">
        {sale ? (
          <p className="property-price">
            {formatXaf(property.price)}
            {property.negotiable ? <span className="per-month"> - negotiable</span> : null}
          </p>
        ) : (
          <p className="property-price">
            {multi && summary ? <span className="per-month">from </span> : null}
            {formatXaf(multi && property.unitCounts?.fromRent ? property.unitCounts.fromRent : property.price)}
            <span className="per-month"> / month</span>
          </p>
        )}
        <h3 className="property-title">
          <Link to={`/properties/${property.id}`}>{property.title}</Link>
        </h3>
        <p className="property-place">{place}</p>

        {sale ? (
          <ul className="property-facts">
            <li>{propertyTypeLabel(property.propertyType) || titleCase(property.propertyType)}</li>
            {property.plotSqm ? <li>{formatPlot(property.plotSqm)} plot</li> : null}
            {property.propertyType !== 'land' && property.rooms ? (
              <li>
                {property.rooms} room{property.rooms === 1 ? '' : 's'}
              </li>
            ) : null}
            {property.landTitle === 'titled' ? (
              <li className="facts-good">Land certificate</li>
            ) : property.landTitle && property.landTitle !== 'none' ? (
              <li>{landTitles.find((title) => title.slug === property.landTitle)?.label || property.landTitle}</li>
            ) : null}
          </ul>
        ) : multi ? (
          <ul className="property-facts">
            <li>{lettingOf(property).short}</li>
            {summary ? <li className={property.unitCounts.available > 0 ? 'facts-good' : ''}>{summary}</li> : null}
          </ul>
        ) : (
          <ul className="property-facts">
            <li>{property.rooms === 0 ? 'No rooms' : `${property.rooms} room${property.rooms === 1 ? '' : 's'}`}</li>
            <li>{property.bathrooms} bath</li>
            {property.areaSqm ? <li>{property.areaSqm} m2</li> : null}
            <li>{propertyTypeLabel(property.propertyType) || titleCase(property.propertyType)}</li>
          </ul>
        )}

        {footer}
      </div>
    </article>
  );
};

export default PropertyCard;
